import { Form } from 'antd';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import storage from 'utils/sessionStorage';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import ModalQRPayment, { useModalQRPaymentRef } from './Modal';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { calcRemaining, IFormMultiPaymentValues } from '../helpers';
import { sum } from 'lodash';
type Props = {
  sendingData: (type: string, data?: any) => void
};
type Ref = {
  requestQRPayment: (method: string, amount: number) => void;
};
export const useQRPaymentHandlerRef = () => useRef<Ref>(null);
const QRPaymentHandler = forwardRef<Ref, Props>(({ sendingData }, ref) => {
  const QRPaymentRef = useModalQRPaymentRef();
  const masterForm = Form.useFormInstance();
  const socketContext = useSocketContext();

  const requestQRPayment: Ref['requestQRPayment'] = (method, amount) => {
    const billId = masterForm.getFieldValue(['detail', 'billId']);
    const detail: IBillInfoResData = masterForm.getFieldValue('detail');
    const values: IFormMultiPaymentValues = masterForm.getFieldsValue();
    if (!billId) return;
    const remaining = calcRemaining(values);
    sendingData(TypePaymentPassData.PASS_DATA, {
      cash: values.CASH || 0,
      remaining: remaining || 0,
      chanceCash: values.chance ? -values.chance : 0,
      loyalty: values.LOYALTY_POINT || 0,
      giftCard: values.GIFT_CARD || 0,
      card: values.CREDIT_CARD || 0,
      multiPay: 1,
      QRMethod: method,
      checkOthers: sum([values.CHECK || 0, values.CASH_APPS || 0, values.VENMO || 0, values.PAYPAL || 0, values.ZELLE || 0]),
      check: values.CHECK || 0,
      cashApp: values.CASH_APPS || 0,
      venmo: values.VENMO || 0,
      paypal: values.PAYPAL || 0,
      zelle: values.ZELLE || 0,

      totalValue: detail?.total,
      subTotal: detail?.subTotal || 0,
      cardFee: values.cardFee || 0,
      saleTax: detail?.saleTax || 0,
      usedTax: detail?.useTax || 0,
      discount: (detail?.discount || 0) + (detail?.discountItem || 0) + (values.cashIncentiveValue || 0) + (values.checkIncentiveValue || 0),
    } as ICustomerPaymentDetail);

    const items = detail?.items?.map((o) => ({
      name: o.itemName,
      price: o.price,
      quantity: o.quantity || 0,
      discount: o.discount || 0,
      requiredMember: o.requiredMember,
    })) || [];

    QRPaymentRef.current?.open({
      paymentType: method,
      amount,
      detail,
      items
    });
  };

  const closeCustomerQRScreen = () => {
    const billId = masterForm.getFieldValue(['detail', 'billId']);
    if (!billId) return;
    if (!socketContext.isConnect()) return;

    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.QR_MULTIPLE_PAYMENT_DONE,
    };
    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };

  const handleDoneQRPayment = async (otherMethod: string) => {
    masterForm.setFieldValue(`isValid_${otherMethod}`, true);
    closeCustomerQRScreen();
    setTimeout(() => masterForm.submit(), 100);
  };

  useImperativeHandle(ref, () => ({ requestQRPayment }));

  return (
    <ModalQRPayment
      ref={QRPaymentRef}
      onDone={handleDoneQRPayment}
      onCancel={closeCustomerQRScreen}
    />
  );
});
QRPaymentHandler.displayName = 'QRPaymentHandler';
export default QRPaymentHandler;
